import React, { useEffect, useState } from 'react';
import { ListFilter } from 'lucide-react';
import { Button } from '~/shadcn/components/ui/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '~/shadcn/components/ui/DropdownMenu';
import { TextFilterMenuItem, NumberFilterMenuItem, DateRangeFilterMenuItem, SelectFilterMenuItem } from '.';
import { has, isNil, isString, trim, isArray, isDate, isPlainObject, isEqual } from 'lodash';
import { FilterType } from './FilterType';

const MENU_ITEM_REGISTRY = {
    [FilterType.TEXT]: TextFilterMenuItem,
    [FilterType.NUMBER]: NumberFilterMenuItem,
    [FilterType.SELECT]: SelectFilterMenuItem,
    [FilterType.DATE_RANGE]: DateRangeFilterMenuItem,
};

const getValue = (obj, key, fallback) => (obj && has(obj, key) ? obj[key] : fallback);

export function EntityFilterDropDownMenu({
    title = 'Filters',
    filterConfigs = [],
    initialValues = {},
    onChange,
    onApply,
    onDiscard,
    hasPendingChanges = false,
    autoApply = false,
    buttonLabel = 'Filters',
    className = '',
    open: controlledOpen,
    onOpenChange,
}) {
    const [values, setValues] = useState(() => ({ ...initialValues }));
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = controlledOpen !== undefined;
    const menuOpen = isControlled ? controlledOpen : internalOpen;
    const setOpen = (next) => {
        if (onOpenChange) onOpenChange(next);
        if (!isControlled) setInternalOpen(next);
    };

    useEffect(() => {
        setValues((prev) => {
            if (isEqual(prev, initialValues)) return prev;
            return { ...initialValues };
        });
    }, [initialValues]);

    const isEmptyValue = (val) => {
        if (isNil(val)) return true;
        if (isString(val)) return trim(val) === '';
        if (isArray(val)) return val.length === 0;
        if (isDate(val)) return false;
        if (isPlainObject(val)) {
            const entries = Object.entries(val);
            if (entries.length === 0) return true;
            for (const [, v] of entries) {
                if (isDate(v)) return false;
                if (isArray(v) && v.length > 0) return false;
                if (isString(v) && trim(v) !== '') return false;
                if (typeof v === 'number') return false; // keep numbers incl. 0
                if (!isNil(v) && isPlainObject(v)) {
                    if (!isEmptyValue(v) && Object.keys(v).length > 0) return false;
                }
                if (v !== undefined && v !== null && v !== '') return false;
            }
            return true;
        }
        return false;
    };

    const handleChange = (key, val, shouldCloseAfterChange = false) => {
        const next = { ...values };
        if (isEmptyValue(val)) delete next[key];
        else next[key] = val;
        setValues(next);
        if (onChange) onChange(next);
        if (autoApply && shouldCloseAfterChange) {
            setOpen(false);
        }
    };

    return (
        <div className={className}>
            <div className="flex items-center gap-2">
                <DropdownMenu open={menuOpen} onOpenChange={setOpen}>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="iconSm" aria-label={buttonLabel}>
                            <ListFilter aria-hidden="true" />
                            <span className="sr-only">{buttonLabel}</span>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[320px] p-0">
                        <DropdownMenuLabel className="px-2 py-2">{title}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {filterConfigs.map((cfg) => {
                            const value = getValue(values, cfg.key, undefined);
                            const MenuItem = cfg.menuComponent || MENU_ITEM_REGISTRY[cfg.type];
                            if (!MenuItem) return null;

                            // Unified select type with optional multi flag and optional search
                            const isSelectLike = cfg.type === FilterType.SELECT;
                            const multi = cfg.multi === true;
                            const enableSearch = cfg.enableSearch === true;
                            const isSingleDate = cfg.type === FilterType.DATE_RANGE && cfg.range === false;
                            const isText = cfg.type === FilterType.TEXT;
                            const isNumber = cfg.type === FilterType.NUMBER;
                            const shouldCloseAfterChange =
                                (isSelectLike && !multi) || isSingleDate || isText || isNumber;

                            return (
                                <MenuItem
                                    key={cfg.key}
                                    id={cfg.key}
                                    label={cfg.label}
                                    placeholder={cfg.placeholder}
                                    options={cfg.options}
                                    value={value}
                                    onChange={(v) => handleChange(cfg.key, v, shouldCloseAfterChange)}
                                    {...(isSelectLike ? { multi, enableSearch } : {})}
                                    {...(cfg.menuProps || {})}
                                    {...(cfg.type === FilterType.DATE_RANGE
                                        ? { range: cfg.range !== false, includeTime: cfg.includeTime === true }
                                        : {})}
                                />
                            );
                        })}
                        <DropdownMenuSeparator />
                        {hasPendingChanges && (
                            <div className="px-2 py-2 flex justify-end gap-2">
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                        if (onDiscard) onDiscard();
                                        // keep menu open so user can continue editing if desired
                                    }}
                                >
                                    Discard Changes
                                </Button>
                                {!autoApply && (
                                    <Button
                                        size="sm"
                                        onClick={() => {
                                            if (onApply) onApply(values);
                                            setOpen(false);
                                        }}
                                    >
                                        Apply
                                    </Button>
                                )}
                            </div>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}
